import React, { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";
import { ChevronDown, Check, AlertTriangle, RefreshCw, Eye, Camera, X, ChevronLeft, ChevronRight } from "lucide-react";
import { formatDate } from "@/lib/dates";

type InspectionItem = {
  id: string;
  inspection_id: string;
  category: string;
  item_key: string;
  label: string;
  status: string;
  note: string;
  resolved_at: string | null;
  resolved_by: string | null;
};

type InspectionPhoto = {
  id: string;
  inspection_id: string;
  category: string;
  photo_type: string;
  storage_path: string;
};

type Inspection = {
  id: string;
  vehicle_number: string;
  driver_name: string;
  created_at: string;
  overall_status: string;
  notes: string;
  inspection_type: string | null;
  items?: InspectionItem[];
  photos?: InspectionPhoto[];
};

type Props = {
  vehicleNumber: string;
  latestDriverInspection: Inspection | null;
  latestAdminInspection: Inspection | null;
  onRefresh?: () => void;
};

function daysBetween(dateIsoOrTs: string) {
  const d = new Date(dateIsoOrTs);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

const STATUS_COLORS: Record<string, string> = {
  OK: "text-green-400 border-green-400/50",
  "Needs Attention": "text-yellow-400 border-yellow-400/50",
  "Needs Fixing Soon": "text-orange-400 border-orange-400/50",
  Unsafe: "text-red-400 border-red-400/50",
};

const STATUS_BG_COLORS: Record<string, string> = {
  OK: "bg-green-500/10",
  "Needs Attention": "bg-yellow-500/10",
  "Needs Fixing Soon": "bg-orange-500/10",
  Unsafe: "bg-red-500/10",
};

const BUCKET_VEHICLE_INSPECTIONS = "vehicle-inspections";

const VehicleInspectionDetailsDropdown: React.FC<Props> = ({
  vehicleNumber,
  latestDriverInspection,
  latestAdminInspection,
  onRefresh,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState<Inspection | null>(null);
  const [inspectionType, setInspectionType] = useState<"driver" | "admin">("driver");
  const [enlargedImageIndex, setEnlargedImageIndex] = useState<number | null>(null);
  const [markingOk, setMarkingOk] = useState<string | null>(null);

  const loadInspectionDetails = async (inspection: Inspection) => {
    if (inspection.items && inspection.photos) {
      setSelectedInspection(inspection);
      return;
    }

    setLoading(true);
    try {
      // Load items
      const { data: itemsData, error: itemsError } = await supabase
        .from("vehicle_inspection_items")
        .select("id, inspection_id, category, item_key, label, status, note, resolved_at, resolved_by")
        .eq("inspection_id", inspection.id)
        .order("category");

      if (itemsError) throw itemsError;

      // Load photos
      const { data: photosData, error: photosError } = await supabase
        .from("vehicle_inspection_photos")
        .select("id, inspection_id, category, photo_type, storage_path")
        .eq("inspection_id", inspection.id);

      if (photosError) throw photosError;

      setSelectedInspection({
        ...inspection,
        items: itemsData || [],
        photos: photosData || [],
      });
    } catch (e: any) {
      toast({ title: "Error loading details", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      const inspection = inspectionType === "driver" ? latestDriverInspection : latestAdminInspection;
      if (inspection) {
        loadInspectionDetails(inspection);
      } else {
        setSelectedInspection(null);
      }
    }
  }, [open, inspectionType, latestDriverInspection, latestAdminInspection]);

  const handleMarkItemOk = async (item: InspectionItem) => {
    setMarkingOk(item.id);
    try {
      const driverName = localStorage.getItem("driver_name") || "Admin";
      const now = new Date().toISOString();

      const { error } = await supabase
        .from("vehicle_inspection_items")
        .update({
          status: "OK",
          resolved_at: now,
          resolved_by: driverName,
        })
        .eq("id", item.id);

      if (error) throw error;

      setSelectedInspection((prev) => {
        if (!prev) return prev;
        const updatedItems = prev.items?.map((i) =>
          i.id === item.id ? { ...i, status: "OK", resolved_at: now, resolved_by: driverName } : i
        );
        return { ...prev, items: updatedItems };
      });

      // Check if all items are now OK and update overall status
      const updatedItems = selectedInspection?.items?.map((i) =>
        i.id === item.id ? { ...i, status: "OK" } : i
      );
      const allOk = updatedItems?.every((i) => i.status === "OK");

      if (allOk && item.inspection_id) {
        await supabase
          .from("vehicle_inspections")
          .update({ overall_status: "OK" })
          .eq("id", item.inspection_id);

        setSelectedInspection((prev) => (prev ? { ...prev, overall_status: "OK" } : prev));
      }

      toast({ title: "Item marked as OK" });
      onRefresh?.();
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "Unknown error";
      toast({ title: "Error updating item", description: message, variant: "destructive" });
    } finally {
      setMarkingOk(null);
    }
  };

  const getPhotoUrl = (storagePath: string): string => {
    if (!storagePath) return "";
    const { data } = supabase.storage.from(BUCKET_VEHICLE_INSPECTIONS).getPublicUrl(storagePath);
    return data?.publicUrl || "";
  };

  const hasAnyInspection = latestDriverInspection || latestAdminInspection;
  const hasIssues = (latestDriverInspection?.overall_status && latestDriverInspection.overall_status !== "OK") ||
                   (latestAdminInspection?.overall_status && latestAdminInspection.overall_status !== "OK");

  // Group items by category
  const itemsByCategory = selectedInspection?.items?.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, InspectionItem[]>) || {};

  const categoryOrder = ["Fluids", "Oil", "Brakes", "Camera", "Lights", "Tyres", "Interior", "Exterior", "Safety"];
  const sortedCategories = Object.keys(itemsByCategory).sort((a, b) => {
    const aIdx = categoryOrder.indexOf(a);
    const bIdx = categoryOrder.indexOf(b);
    if (aIdx === -1 && bIdx === -1) return a.localeCompare(b);
    if (aIdx === -1) return 1;
    if (bIdx === -1) return -1;
    return aIdx - bIdx;
  });

  // Photo navigation functions
  const photos = selectedInspection?.photos || [];
  const totalPhotos = photos.length;

  const handlePreviousPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (enlargedImageIndex !== null && enlargedImageIndex > 0) {
      setEnlargedImageIndex(enlargedImageIndex - 1);
    }
  };

  const handleNextPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (enlargedImageIndex !== null && enlargedImageIndex < totalPhotos - 1) {
      setEnlargedImageIndex(enlargedImageIndex + 1);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (enlargedImageIndex === null) return;
    if (e.key === "ArrowLeft" && enlargedImageIndex > 0) {
      setEnlargedImageIndex(enlargedImageIndex - 1);
    } else if (e.key === "ArrowRight" && enlargedImageIndex < totalPhotos - 1) {
      setEnlargedImageIndex(enlargedImageIndex + 1);
    } else if (e.key === "Escape") {
      setEnlargedImageIndex(null);
    }
  };

  useEffect(() => {
    if (enlargedImageIndex !== null) {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [enlargedImageIndex, totalPhotos]);

  const currentPhoto = enlargedImageIndex !== null ? photos[enlargedImageIndex] : null;
  const currentPhotoUrl = currentPhoto ? getPhotoUrl(currentPhoto.storage_path) : "";

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={`h-auto py-1 px-2 justify-start font-normal ${
              hasIssues ? "text-yellow-400" : hasAnyInspection ? "text-muted-foreground" : "text-red-400"
            }`}
          >
            {hasAnyInspection ? (
              <div className="flex items-center gap-1">
                <Eye className="w-3 h-3" />
                <span className="text-xs">Details</span>
                {hasIssues && <AlertTriangle className="w-3 h-3 text-yellow-400" />}
                <ChevronDown className="w-3 h-3" />
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <span className="text-xs">No data</span>
                <RefreshCw className="w-3 h-3" />
              </div>
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-[420px] p-0" align="start">
          <div className="p-3 border-b">
            <div className="font-semibold text-sm">
              Inspection Details - {vehicleNumber}
            </div>
            <div className="text-xs text-muted-foreground">
              View all checked items and photos
            </div>
          </div>

          {/* Inspection Type Toggle */}
          <div className="p-3 border-b flex gap-2">
            <Button
              variant={inspectionType === "driver" ? "default" : "outline"}
              size="sm"
              onClick={() => setInspectionType("driver")}
              className="flex-1"
            >
              Driver Check
              {latestDriverInspection && (
                <Badge variant="outline" className="ml-2 text-xs">
                  {formatDate(latestDriverInspection.created_at)}
                </Badge>
              )}
            </Button>
            <Button
              variant={inspectionType === "admin" ? "default" : "outline"}
              size="sm"
              onClick={() => setInspectionType("admin")}
              className="flex-1"
            >
              Admin Check
              {latestAdminInspection && (
                <Badge variant="outline" className="ml-2 text-xs">
                  {formatDate(latestAdminInspection.created_at)}
                </Badge>
              )}
            </Button>
          </div>

          <div className="max-h-[400px] overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-muted-foreground">Loading...</div>
            ) : !selectedInspection ? (
              <div className="p-4 text-center text-muted-foreground">
                No {inspectionType} inspection found
              </div>
            ) : (
              <div className="p-3 space-y-4">
                {/* Inspection Header */}
                <div className="bg-muted/30 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge
                        variant={selectedInspection.overall_status !== "OK" ? "destructive" : "outline"}
                        className={selectedInspection.overall_status === "OK" ? "text-green-400 border-green-400/50" : ""}
                      >
                        {selectedInspection.overall_status}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {selectedInspection.inspection_type === "admin" ? "Admin" : "Driver"}
                      </Badge>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatDate(selectedInspection.created_at)}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    By: {selectedInspection.driver_name || "Unknown"}
                  </div>
                  {selectedInspection.notes && (
                    <div className="text-xs text-muted-foreground mt-1 border-t pt-1">
                      Notes: {selectedInspection.notes}
                    </div>
                  )}
                </div>

                {/* Photos Section */}
                {selectedInspection.photos && selectedInspection.photos.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-xs font-semibold flex items-center gap-1">
                      <Camera className="w-3 h-3" />
                      Photos ({selectedInspection.photos.length})
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      {selectedInspection.photos.map((photo, index) => {
                        const url = getPhotoUrl(photo.storage_path);
                        return (
                          <div
                            key={photo.id}
                            className="relative group cursor-pointer"
                            onClick={() => setEnlargedImageIndex(index)}
                          >
                            <div className="aspect-square rounded-lg overflow-hidden border border-border bg-muted">
                              <img
                                src={url}
                                alt={photo.photo_type}
                                className="w-full h-full object-cover transition-transform group-hover:scale-110"
                              />
                            </div>
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                              <Eye className="w-4 h-4 text-white" />
                            </div>
                            <div className="text-[10px] text-muted-foreground text-center mt-1 truncate">
                              {photo.photo_type.replace(/_/g, " ")}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Items by Category */}
                {sortedCategories.map((category) => (
                  <div key={category} className="space-y-1">
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      {category}
                    </div>
                    <div className="space-y-1">
                      {itemsByCategory[category].map((item) => (
                        <div
                          key={item.id}
                          className={`rounded-lg p-2 border ${STATUS_BG_COLORS[item.status] || "bg-muted/30"} ${
                            item.status === "OK" ? "border-green-500/20" : "border-border"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              {item.status === "OK" ? (
                                <Check className="w-3 h-3 text-green-400 shrink-0" />
                              ) : (
                                <AlertTriangle className={`w-3 h-3 shrink-0 ${STATUS_COLORS[item.status]?.split(" ")[0] || "text-yellow-400"}`} />
                              )}
                              <span className="text-sm truncate">{item.label}</span>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <Badge
                                variant="outline"
                                className={`text-xs ${STATUS_COLORS[item.status] || ""}`}
                              >
                                {item.status}
                              </Badge>
                              {item.status !== "OK" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-6 px-2 text-green-400 border-green-400/50 hover:bg-green-400/10"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMarkItemOk(item);
                                  }}
                                  disabled={markingOk === item.id}
                                >
                                  {markingOk === item.id ? (
                                    <RefreshCw className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <>
                                      <Check className="w-3 h-3 mr-1" />
                                      Mark OK
                                    </>
                                  )}
                                </Button>
                              )}
                            </div>
                          </div>
                          {item.note && (
                            <div className="text-xs text-muted-foreground mt-1 pl-5">
                              Note: {item.note}
                            </div>
                          )}
                          {item.resolved_at && (
                            <div className="text-xs text-green-400 mt-1 pl-5">
                              Resolved {formatDate(item.resolved_at)}
                              {item.resolved_by && ` by ${item.resolved_by}`}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Summary */}
                {selectedInspection.items && (
                  <div className="border-t pt-3 mt-3">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Total items checked:</span>
                      <span className="font-medium">{selectedInspection.items.length}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Items OK:</span>
                      <span className="font-medium text-green-400">
                        {selectedInspection.items.filter((i) => i.status === "OK").length}
                      </span>
                    </div>
                    {selectedInspection.items.filter((i) => i.status !== "OK").length > 0 && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Items needing attention:</span>
                        <span className="font-medium text-yellow-400">
                          {selectedInspection.items.filter((i) => i.status !== "OK").length}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Enlarged Image Modal with Navigation */}
      {enlargedImageIndex !== null && currentPhoto && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex flex-col items-center justify-center"
          onClick={() => setEnlargedImageIndex(null)}
        >
          {/* Top Navigation Bar */}
          <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3 bg-black/50">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20 disabled:opacity-30"
                onClick={handlePreviousPhoto}
                disabled={enlargedImageIndex === 0}
              >
                <ChevronLeft className="w-5 h-5 mr-1" />
                Previous
              </Button>
              <span className="text-white text-sm">
                {enlargedImageIndex + 1} of {totalPhotos}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20 disabled:opacity-30"
                onClick={handleNextPhoto}
                disabled={enlargedImageIndex === totalPhotos - 1}
              >
                Next
                <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20"
              onClick={() => setEnlargedImageIndex(null)}
            >
              <X className="w-5 h-5 mr-1" />
              Close
            </Button>
          </div>

          {/* Main Image */}
          <div className="flex-1 flex items-center justify-center p-4 pt-16 pb-16 w-full">
            <img
              src={currentPhotoUrl}
              alt={currentPhoto.photo_type}
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Photo Type Label */}
          <div className="absolute bottom-4 left-0 right-0 text-center">
            <span className="text-white bg-black/50 px-4 py-2 rounded-lg text-sm">
              {currentPhoto.photo_type.replace(/_/g, " ")}
            </span>
          </div>

          {/* Side Navigation Arrows */}
          {enlargedImageIndex > 0 && (
            <button
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
              onClick={handlePreviousPhoto}
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
          )}
          {enlargedImageIndex < totalPhotos - 1 && (
            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
              onClick={handleNextPhoto}
            >
              <ChevronRight className="w-8 h-8" />
            </button>
          )}
        </div>
      )}
    </>
  );
};

export default VehicleInspectionDetailsDropdown;
